import demoSeed from '../pages/Dashboard/QualityControl/data/nonConformities.fake.json';
import { isNonConformityOverdue } from '../pages/Dashboard/QualityControl/utils';
import {
  FactoryUnit,
  NonConformityStatus,
} from '../utils/qualityControlConstants';
import { api } from './api';

export type ActionKind = 'corrective' | 'preventive';

export type NonConformitySigner = 'opener' | 'responsible';

export type SignatureStyle =
  | 'cursive-1'
  | 'cursive-2'
  | 'cursive-3'
  | 'elegant-1'
  | 'elegant-2';

export interface SignNonConformityInput {
  name: string;
  cpf: string;
  style: SignatureStyle;
  // Assinatura no aparelho de outra pessoa: login e senha do SAMASC de quem
  // assina (a API confere como no login e não abre sessão).
  nickname?: string;
  password?: string;
}

export interface NonConformityEvidence {
  id: string;
  original_name: string;
  content_type: string;
  size_bytes: number;
  url: string;
}

export interface NonConformityAction {
  id: string;
  action_kind: ActionKind;
  description: string;
  responsible: string;
  due_date: string;
  // Execução da ação (só nas ações já gravadas; rascunho do cadastro não
  // tem): pendente enquanto completed_at é nulo.
  completed_at?: string | null;
  completed_by_user_name?: string | null;
  completion_note?: string | null;
  evidences?: NonConformityEvidence[];
}

export interface NonConformity {
  id: string;
  code: string;
  title: string;
  responsible: string;
  responsible_user_id: string;
  responsible_email: string | null;
  opened_at: string;
  due_date: string;
  status: NonConformityStatus;
  occurrence_types: string[];
  factory_unit: FactoryUnit;
  supplier_or_producer: string | null;
  description: string;
  created_by_user_id: string;
  created_by_user_name: string;
  closed_at: string | null;
  closed_by_user_id: string | null;
  closed_by_user_name: string | null;
  // Assinatura de ciência: quem abriu primeiro, depois o responsável. O CPF
  // chega mascarado pela API (***.456.789-**).
  opener_signature_name: string | null;
  opener_signature_cpf: string | null;
  opener_signature_style: SignatureStyle | null;
  opener_signed_at: string | null;
  responsible_signature_name: string | null;
  responsible_signature_cpf: string | null;
  responsible_signature_style: SignatureStyle | null;
  responsible_signed_at: string | null;
  created_at: string;
  updated_at: string;
  corrective_actions: NonConformityAction[];
  preventive_actions: NonConformityAction[];
  evidences: NonConformityEvidence[];
}

export interface NonConformityIndicators {
  open: number;
  inProgress: number;
  closed: number;
  overdue: number;
  total: number;
}

export interface NonConformityFilters {
  status?: NonConformityStatus;
  overdue?: boolean;
  factoryUnit?: FactoryUnit;
  openedBy?: string;
  openedFrom?: string;
  openedTo?: string;
  closedFrom?: string;
  closedTo?: string;
  search?: string;
}

export interface CreateNonConformityInput {
  title: string;
  // "Quem abriu" (id do usuário). A API grava o nome do cadastro dele; sem
  // o campo, vale o usuário logado.
  opened_by_user_id?: string;
  responsible: string;
  responsible_user_id: string;
  responsible_email: string | null;
  opened_at: string;
  due_date: string;
  occurrence_types: string[];
  factory_unit: FactoryUnit;
  supplier_or_producer: string | null;
  description: string;
  corrective_actions: Omit<NonConformityAction, 'id' | 'action_kind'>[];
  preventive_actions: Omit<NonConformityAction, 'id' | 'action_kind'>[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function requiredString(value: unknown): string {
  return String(value ?? '').trim();
}

function optionalString(value: unknown): string | null {
  return value ? requiredString(value) : null;
}

// Modo demonstração: ação pendente de uma NC aberta (mesmas travas da API).
function findDemoAction(
  items: NonConformity[],
  nonConformityId: string,
  actionId: string,
): NonConformityAction {
  const target = items.find(item => item.id === nonConformityId);
  if (!target) throw new Error('Não conformidade não encontrada.');
  assertEditable(target);

  const action = [
    ...target.corrective_actions,
    ...target.preventive_actions,
  ].find(item => item.id === actionId);
  if (!action) throw new Error('Ação não encontrada nesta Não Conformidade.');
  if (action.completed_at) throw new Error('Esta ação já foi concluída.');
  if (!target.responsible_signed_at) {
    throw new Error(
      'O plano de ação só pode ser executado depois da ciência do responsável.',
    );
  }

  return action;
}

function isOverdue(nonConformity: NonConformity): boolean {
  return isNonConformityOverdue(nonConformity.status, nonConformity.due_date);
}

/**
 * URL abrível de uma evidência. No STORAGE_DRIVER=disk o backend devolve um
 * caminho relativo (/uploads/...), servido pela própria API — mesmo
 * tratamento de getReceiptUrl em generateExpensePDF.ts. S3 já vem absoluta;
 * blob:/data: são do modo demo.
 */
export function getEvidenceUrl(url: string): string {
  if (!url) return '';
  if (/^(https?:|blob:|data:)/.test(url)) return url;
  return `${api.defaults.baseURL}${url.startsWith('/') ? '' : '/'}${url}`;
}

function normalizeAction(value: unknown): NonConformityAction | null {
  if (!isRecord(value)) return null;
  const description = requiredString(value.description);
  const responsible = requiredString(value.responsible);
  const dueDate = requiredString(value.due_date);
  const actionKind =
    value.action_kind === 'preventive' ? 'preventive' : 'corrective';
  if (!description || !responsible || !dueDate) return null;

  return {
    id:
      requiredString(value.id) ||
      `${actionKind}-${Date.now()}-${Math.random()}`,
    action_kind: actionKind,
    description,
    responsible,
    due_date: dueDate,
    completed_at: optionalString(value.completed_at),
    completed_by_user_name: optionalString(value.completed_by_user_name),
    completion_note: optionalString(value.completion_note),
    evidences: Array.isArray(value.evidences)
      ? value.evidences
          .map(normalizeEvidence)
          .filter((e): e is NonConformityEvidence => e !== null)
      : [],
  };
}

function normalizeEvidence(value: unknown): NonConformityEvidence | null {
  if (!isRecord(value)) return null;
  const originalName = requiredString(value.original_name);
  const url = requiredString(value.url);
  if (!originalName || !url) return null;

  return {
    id: requiredString(value.id),
    original_name: originalName,
    content_type: requiredString(value.content_type),
    size_bytes: Number(value.size_bytes) || 0,
    url,
  };
}

export function normalizeNonConformity(value: unknown): NonConformity {
  if (!isRecord(value)) {
    throw new Error('A API retornou uma não conformidade inválida.');
  }

  const id = requiredString(value.id);
  const code = requiredString(value.code);
  const title = requiredString(value.title);
  const responsible = requiredString(value.responsible);
  const openedAt = requiredString(value.opened_at);
  const dueDate = requiredString(value.due_date);
  const description = requiredString(value.description);
  const status = value.status as NonConformityStatus;
  const validStatuses: NonConformityStatus[] = [
    'open',
    'in_progress',
    'closed',
  ];

  if (
    !id ||
    !code ||
    !title ||
    !responsible ||
    !openedAt ||
    !dueDate ||
    !description ||
    !validStatuses.includes(status) ||
    !Array.isArray(value.occurrence_types)
  ) {
    throw new Error(
      'A API retornou uma não conformidade incompleta ou inválida.',
    );
  }

  return {
    id,
    code,
    title,
    responsible,
    responsible_user_id: requiredString(value.responsible_user_id),
    responsible_email: value.responsible_email
      ? requiredString(value.responsible_email)
      : null,
    opened_at: openedAt,
    due_date: dueDate,
    status,
    occurrence_types: value.occurrence_types.map(String),
    factory_unit: (value.factory_unit as FactoryUnit) || 'bovine',
    supplier_or_producer: value.supplier_or_producer
      ? requiredString(value.supplier_or_producer)
      : null,
    description,
    created_by_user_id: requiredString(value.created_by_user_id),
    created_by_user_name: requiredString(value.created_by_user_name),
    closed_at: value.closed_at ? requiredString(value.closed_at) : null,
    closed_by_user_id: value.closed_by_user_id
      ? requiredString(value.closed_by_user_id)
      : null,
    closed_by_user_name: value.closed_by_user_name
      ? requiredString(value.closed_by_user_name)
      : null,
    opener_signature_name: optionalString(value.opener_signature_name),
    opener_signature_cpf: optionalString(value.opener_signature_cpf),
    opener_signature_style: optionalString(
      value.opener_signature_style,
    ) as SignatureStyle | null,
    opener_signed_at: optionalString(value.opener_signed_at),
    responsible_signature_name: optionalString(
      value.responsible_signature_name,
    ),
    responsible_signature_cpf: optionalString(value.responsible_signature_cpf),
    responsible_signature_style: optionalString(
      value.responsible_signature_style,
    ) as SignatureStyle | null,
    responsible_signed_at: optionalString(value.responsible_signed_at),
    created_at: requiredString(value.created_at) || new Date().toISOString(),
    updated_at: requiredString(value.updated_at) || new Date().toISOString(),
    corrective_actions: Array.isArray(value.corrective_actions)
      ? value.corrective_actions
          .map(normalizeAction)
          .filter((a): a is NonConformityAction => a !== null)
      : [],
    preventive_actions: Array.isArray(value.preventive_actions)
      ? value.preventive_actions
          .map(normalizeAction)
          .filter((a): a is NonConformityAction => a !== null)
      : [],
    evidences: Array.isArray(value.evidences)
      ? value.evidences
          .map(normalizeEvidence)
          .filter((e): e is NonConformityEvidence => e !== null)
      : [],
  };
}

function normalizeNonConformityList(payload: unknown): NonConformity[] {
  let rawList: unknown[] | null = null;
  if (Array.isArray(payload)) {
    rawList = payload;
  } else if (isRecord(payload) && Array.isArray(payload.items)) {
    rawList = payload.items;
  }

  if (!rawList) {
    throw new Error(
      'A API não retornou uma lista de não conformidades válida.',
    );
  }

  return rawList.map(normalizeNonConformity);
}

function applyFilters(
  items: NonConformity[],
  filters: NonConformityFilters,
): NonConformity[] {
  return items.filter(item => {
    if (filters.status && item.status !== filters.status) return false;
    if (filters.overdue && !isOverdue(item)) return false;
    if (filters.factoryUnit && item.factory_unit !== filters.factoryUnit)
      return false;
    if (
      filters.openedBy &&
      !item.created_by_user_name
        .toLowerCase()
        .includes(filters.openedBy.toLowerCase())
    )
      return false;
    if (filters.openedFrom && item.opened_at < filters.openedFrom) return false;
    if (filters.openedTo && item.opened_at > filters.openedTo) return false;
    if (
      filters.closedFrom &&
      (!item.closed_at || item.closed_at < filters.closedFrom)
    )
      return false;
    if (
      filters.closedTo &&
      (!item.closed_at || item.closed_at > filters.closedTo)
    )
      return false;
    if (
      filters.search &&
      !`${item.code} ${item.description}`
        .toLowerCase()
        .includes(filters.search.toLowerCase())
    )
      return false;
    return true;
  });
}

function calculateIndicators(items: NonConformity[]): NonConformityIndicators {
  return items.reduce<NonConformityIndicators>(
    (acc, item) => ({
      open: acc.open + (item.status === 'open' ? 1 : 0),
      inProgress: acc.inProgress + (item.status === 'in_progress' ? 1 : 0),
      closed: acc.closed + (item.status === 'closed' ? 1 : 0),
      overdue: acc.overdue + (isOverdue(item) ? 1 : 0),
      total: acc.total + 1,
    }),
    { open: 0, inProgress: 0, closed: 0, overdue: 0, total: 0 },
  );
}

/**
 * Demo store: modo `?demo=1` roda 100% no cliente enquanto o backend real
 * (contrato em docs/CONTROLE_QUALIDADE_MVP.md) não existe. Estado persiste em
 * localStorage para sobreviver a reloads, semeado por nonConformities.fake.json.
 * Não usar em produção — é só para prototipagem/validação de fluxo.
 */
const DEMO_STORAGE_KEY = '@Samasc:quality-control-demo-store';

function loadDemoStore(): NonConformity[] {
  try {
    const raw = window.localStorage.getItem(DEMO_STORAGE_KEY);
    if (!raw) {
      const seeded = (demoSeed as unknown[]).map(normalizeNonConformity);
      window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(seeded));
      return seeded;
    }
    return (JSON.parse(raw) as unknown[]).map(normalizeNonConformity);
  } catch {
    return (demoSeed as unknown[]).map(normalizeNonConformity);
  }
}

function saveDemoStore(items: NonConformity[]): void {
  window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(items));
}

function assertEditable(nonConformity: NonConformity): void {
  if (nonConformity.status === 'closed') {
    throw new Error(
      'Esta não conformidade já foi concluída e não pode mais ser alterada.',
    );
  }
}

function generateDemoCode(items: NonConformity[]): string {
  const year = new Date().getFullYear();
  const countThisYear = items.filter(item =>
    item.code.includes(String(year)),
  ).length;
  return `NC-${year}-${String(countThisYear + 1).padStart(3, '0')}`;
}

export const qualityControlService = {
  /**
   * Apaga o demo store salvo em localStorage, forçando a próxima leitura a
   * re-semear a partir de nonConformities.fake.json. Usado pelo botão
   * "Resetar dados de demonstração" — necessário porque o seed só é aplicado
   * automaticamente quando a chave ainda não existe.
   */
  resetDemoStore(): void {
    window.localStorage.removeItem(DEMO_STORAGE_KEY);
  },

  async list(
    filters: NonConformityFilters = {},
    demo = false,
  ): Promise<NonConformity[]> {
    if (demo) return applyFilters(loadDemoStore(), filters);

    const response = await api.get('/quality-control/non-conformities', {
      params: filters,
    });
    return normalizeNonConformityList(response.data);
  },

  async getById(id: string, demo = false): Promise<NonConformity> {
    if (demo) {
      const found = loadDemoStore().find(item => item.id === id);
      if (!found) throw new Error('Não conformidade não encontrada.');
      return found;
    }

    const response = await api.get(`/quality-control/non-conformities/${id}`);
    return normalizeNonConformity(response.data);
  },

  /**
   * Exclusão definitiva de uma NC — restrita a administradores (access_full)
   * na UI. Ao contrário das demais operações de escrita, funciona mesmo com
   * `status === 'closed'`: é a válvula de escape para corrigir uma NC criada
   * por engano, mesmo depois de concluída.
   */
  async delete(id: string, demo = false): Promise<void> {
    if (demo) {
      const items = loadDemoStore();
      const exists = items.some(item => item.id === id);
      if (!exists) throw new Error('Não conformidade não encontrada.');
      saveDemoStore(items.filter(item => item.id !== id));
      return;
    }

    await api.delete(`/quality-control/non-conformities/${id}`);
  },

  async getIndicators(demo = false): Promise<NonConformityIndicators> {
    if (demo) return calculateIndicators(loadDemoStore());

    const response = await api.get('/quality-control/indicators');
    if (!isRecord(response.data)) {
      throw new Error('A API retornou indicadores inválidos.');
    }
    return {
      open: Number(response.data.open) || 0,
      inProgress: Number(response.data.inProgress) || 0,
      closed: Number(response.data.closed) || 0,
      overdue: Number(response.data.overdue) || 0,
      total: Number(response.data.total) || 0,
    };
  },

  async create(
    input: CreateNonConformityInput,
    userId: string,
    userName: string,
    demo = false,
  ): Promise<NonConformity> {
    if (demo) {
      const items = loadDemoStore();
      const now = new Date().toISOString();
      const created: NonConformity = {
        id: `demo-${Date.now()}`,
        code: generateDemoCode(items),
        title: input.title,
        responsible: input.responsible,
        responsible_user_id: input.responsible_user_id,
        responsible_email: input.responsible_email,
        opened_at: input.opened_at,
        due_date: input.due_date,
        status: 'open',
        occurrence_types: input.occurrence_types,
        factory_unit: input.factory_unit,
        supplier_or_producer: input.supplier_or_producer,
        description: input.description,
        created_by_user_id: userId,
        created_by_user_name: userName,
        closed_at: null,
        closed_by_user_id: null,
        closed_by_user_name: null,
        opener_signature_name: null,
        opener_signature_cpf: null,
        opener_signature_style: null,
        opener_signed_at: null,
        responsible_signature_name: null,
        responsible_signature_cpf: null,
        responsible_signature_style: null,
        responsible_signed_at: null,
        created_at: now,
        updated_at: now,
        corrective_actions: input.corrective_actions.map((action, index) => ({
          ...action,
          id: `corrective-${Date.now()}-${index}`,
          action_kind: 'corrective',
        })),
        preventive_actions: input.preventive_actions.map((action, index) => ({
          ...action,
          id: `preventive-${Date.now()}-${index}`,
          action_kind: 'preventive',
        })),
        evidences: [],
      };
      saveDemoStore([created, ...items]);
      return created;
    }

    // O e-mail ao responsável fica para notifyResponsible(), chamado depois
    // de enviar as evidências, para ele já sair com elas.
    const response = await api.post('/quality-control/non-conformities', {
      ...input,
      notify_after_evidences: true,
    });
    return normalizeNonConformity(response.data);
  },

  /** Dispara o e-mail de abertura (com as evidências já enviadas). */
  async notifyResponsible(
    nonConformityId: string,
    demo = false,
  ): Promise<void> {
    if (demo) return;

    await api.post(
      `/quality-control/non-conformities/${nonConformityId}/notify-responsible`,
    );
  },

  async uploadEvidence(
    nonConformityId: string,
    file: File,
    demo = false,
  ): Promise<NonConformityEvidence> {
    if (demo) {
      const items = loadDemoStore();
      const target = items.find(item => item.id === nonConformityId);
      if (!target) throw new Error('Não conformidade não encontrada.');
      assertEditable(target);

      const evidence: NonConformityEvidence = {
        id: `evidence-${Date.now()}`,
        original_name: file.name,
        content_type: file.type,
        size_bytes: file.size,
        url: URL.createObjectURL(file),
      };
      target.evidences = [...target.evidences, evidence];
      target.updated_at = new Date().toISOString();
      saveDemoStore(items);
      return evidence;
    }

    const formData = new FormData();
    formData.append('evidence', file);
    const response = await api.post(
      `/quality-control/non-conformities/${nonConformityId}/evidences`,
      formData,
    );
    return response.data;
  },

  async removeEvidence(
    nonConformityId: string,
    evidenceId: string,
    demo = false,
  ): Promise<void> {
    if (demo) {
      const items = loadDemoStore();
      const target = items.find(item => item.id === nonConformityId);
      if (!target) throw new Error('Não conformidade não encontrada.');
      assertEditable(target);
      target.evidences = target.evidences.filter(e => e.id !== evidenceId);
      saveDemoStore(items);
      return;
    }

    await api.delete(
      `/quality-control/non-conformities/${nonConformityId}/evidences/${evidenceId}`,
    );
  },

  async sign(
    nonConformityId: string,
    signer: NonConformitySigner,
    input: SignNonConformityInput,
    demo = false,
  ): Promise<NonConformity> {
    if (demo) {
      const items = loadDemoStore();
      const target = items.find(item => item.id === nonConformityId);
      if (!target) throw new Error('Não conformidade não encontrada.');
      assertEditable(target);

      const digits = input.cpf.replace(/\D/g, '');
      Object.assign(target, {
        [`${signer}_signature_name`]: input.name,
        [`${signer}_signature_cpf`]: `***.${digits.slice(3, 6)}.${digits.slice(
          6,
          9,
        )}-**`,
        [`${signer}_signature_style`]: input.style,
        [`${signer}_signed_at`]: new Date().toISOString(),
      });
      saveDemoStore(items);
      return target;
    }

    const response = await api.post(
      `/quality-control/non-conformities/${nonConformityId}/signatures/${signer}`,
      input,
    );
    return normalizeNonConformity(response.data);
  },

  /** PDF gerado na hora pela API (mesmo modelo do relatório de despesas). */
  async downloadPdf(nonConformityId: string): Promise<Blob> {
    const response = await api.get(
      `/quality-control/non-conformities/${nonConformityId}/pdf`,
      { responseType: 'blob' },
    );
    return response.data;
  },

  async updateStatus(
    nonConformityId: string,
    status: 'in_progress' | 'closed',
    user: { id: string; name: string },
    demo = false,
  ): Promise<NonConformity> {
    if (demo) {
      const items = loadDemoStore();
      const target = items.find(item => item.id === nonConformityId);
      if (!target) throw new Error('Não conformidade não encontrada.');
      assertEditable(target);
      if (
        status === 'closed' &&
        (!target.opener_signed_at || !target.responsible_signed_at)
      ) {
        throw new Error(
          'A NC só pode ser concluída depois das assinaturas de quem abriu e do responsável.',
        );
      }
      if (
        status === 'closed' &&
        [...target.corrective_actions, ...target.preventive_actions].some(
          action => !action.completed_at,
        )
      ) {
        throw new Error(
          'A NC só pode ser concluída depois que todas as ações do plano de ação forem concluídas.',
        );
      }

      target.status = status;
      target.updated_at = new Date().toISOString();
      if (status === 'closed') {
        target.closed_at = new Date().toISOString();
        target.closed_by_user_id = user.id;
        target.closed_by_user_name = user.name;
      }
      saveDemoStore(items);
      return target;
    }

    const response = await api.post(
      `/quality-control/non-conformities/${nonConformityId}/status`,
      { status },
    );
    return normalizeNonConformity(response.data);
  },

  /** Evidência da execução de uma ação do plano (ação ainda pendente). */
  async uploadActionEvidence(
    nonConformityId: string,
    actionId: string,
    file: File,
    demo = false,
  ): Promise<NonConformityEvidence> {
    if (demo) {
      const items = loadDemoStore();
      const action = findDemoAction(items, nonConformityId, actionId);
      const evidence: NonConformityEvidence = {
        id: `evidence-${Date.now()}`,
        original_name: file.name,
        content_type: file.type,
        size_bytes: file.size,
        url: URL.createObjectURL(file),
      };
      action.evidences = [...(action.evidences || []), evidence];
      saveDemoStore(items);
      return evidence;
    }

    const formData = new FormData();
    formData.append('evidence', file);
    const response = await api.post(
      `/quality-control/non-conformities/${nonConformityId}/actions/${actionId}/evidences`,
      formData,
    );
    return response.data;
  },

  async removeActionEvidence(
    nonConformityId: string,
    actionId: string,
    evidenceId: string,
    demo = false,
  ): Promise<void> {
    if (demo) {
      const items = loadDemoStore();
      const action = findDemoAction(items, nonConformityId, actionId);
      action.evidences = (action.evidences || []).filter(
        e => e.id !== evidenceId,
      );
      saveDemoStore(items);
      return;
    }

    await api.delete(
      `/quality-control/non-conformities/${nonConformityId}/actions/${actionId}/evidences/${evidenceId}`,
    );
  },

  /** Conclui a ação; a API exige ao menos uma evidência já anexada a ela. */
  async completeAction(
    nonConformityId: string,
    actionId: string,
    note: string,
    user: { id: string; name: string },
    demo = false,
  ): Promise<NonConformity> {
    if (demo) {
      const items = loadDemoStore();
      const action = findDemoAction(items, nonConformityId, actionId);
      if (!(action.evidences || []).length) {
        throw new Error(
          'Anexe ao menos uma evidência da execução antes de concluir a ação.',
        );
      }
      Object.assign(action, {
        completed_at: new Date().toISOString(),
        completed_by_user_name: user.name,
        completion_note: note.trim() || null,
      });
      saveDemoStore(items);
      return items.find(item => item.id === nonConformityId) as NonConformity;
    }

    const response = await api.post(
      `/quality-control/non-conformities/${nonConformityId}/actions/${actionId}/complete`,
      { note },
    );
    return normalizeNonConformity(response.data);
  },

  async addAction(
    nonConformityId: string,
    actionKind: ActionKind,
    action: Omit<NonConformityAction, 'id' | 'action_kind'>,
    demo = false,
  ): Promise<NonConformity> {
    if (demo) {
      const items = loadDemoStore();
      const target = items.find(item => item.id === nonConformityId);
      if (!target) throw new Error('Não conformidade não encontrada.');
      assertEditable(target);

      const newAction: NonConformityAction = {
        ...action,
        id: `${actionKind}-${Date.now()}`,
        action_kind: actionKind,
      };
      if (actionKind === 'corrective') {
        target.corrective_actions = [...target.corrective_actions, newAction];
      } else {
        target.preventive_actions = [...target.preventive_actions, newAction];
      }
      target.updated_at = new Date().toISOString();
      saveDemoStore(items);
      return target;
    }

    const endpoint =
      actionKind === 'corrective' ? 'corrective-actions' : 'preventive-actions';
    const response = await api.post(
      `/quality-control/non-conformities/${nonConformityId}/${endpoint}`,
      action,
    );
    return normalizeNonConformity(response.data);
  },
};
